import Api from "@/api/index.ts";
import { IOffer } from "../interfaces/offer";
import { ICategories, IInitProducts, IStores } from "../interfaces/products";
import { factoryProducts } from "@/services/factory/products";
import { AxiosResponse } from "axios";

const initProducts: IInitProducts = {
  products: [],
  categories: [],
  sort: "",
  currentCursor: null,
  hasNext: false,
  hasPrev: false,
  category_id: "",
  productsCount: 0,
  isLangChanging: false,
  stores: [],
  price: {
    max: -1,
    min: -1,
    from: -1,
    to: -1
  },
  isFiltering: false,
  wasFiltered: false // to avoid request on reset if it was not submitted
};

const sortStores = (a, b) => {
  const isDigit = a => "1234567890".includes(a);
  if (!a.store_name || !b.store_name) {
    return 0;
  }
  if (isDigit(a.store_name[0]) && !isDigit(b.store_name[0])) {
    return 1;
  }
  if (!isDigit(a.store_name[0]) && isDigit(b.store_name[0])) {
    return -1;
  }
  return a.store_name.toLowerCase() < b.store_name.toLowerCase() ? -1 : 1;
};

const sendFetchProducts = function({
  commit,
  getters,
  direction,
  category,
  count,
  cursor = null,
  setCursor
}) {
  let products: IOffer[] = [];
  Api.getOffers({
    category_id: category,
    length: count,
    cursor: setCursor ? cursor || getters.getCurrentCursor : null,
    store_name: getters.getSelectedStoreNames,
    price_from: getters.getFromToPrice.from,
    price_to: getters.getFromToPrice.to,
    price_sorting: getters.getSort,
    direction
  }).then(async res => {
    products = factoryProducts(res.data.offers, !getters.getResetButtonDisable);
    await commit("setPrevNext", res.data);
    await commit("setProductsCount", res.data.offersCount);
    await commit("setProducts", products);
    await commit("setCurrentCategory", category);
    await commit("setCurrentCursor", res.data.cursor);
    await commit("setIsFiltering", false);
  });
};

export default {
  namespaced: true,
  state: initProducts,
  actions: {
    setCurrentCategory({ commit }, category_id) {
      commit("setCurrentCategory", category_id);
    },

    async getCategories({ commit }) {
      let categories: [] = [];
      const apiRes: AxiosResponse<any> = await Api.getCategories();
      categories = apiRes.data.categories;
      commit("setParentCategories", categories);
    },

    fetchFilterParams({ commit }, [category]) {
      let stores: any = [];
      commit("setStores", stores);

      stores = Api.getStores(category).then(res => {
        stores = res.data.storesList;
        stores = stores.map(store => Object.assign(store, { selected: false }));
        commit("setMinMaxPrice", [res.data.minPrice, res.data.maxPrice]);
        commit("setStores", stores);
        commit("setSelectedStoresUp");
      });
    },

    setWasFiltered({ commit }, value) {
      commit("setWasFiltered", value);
    },

    fetchProducts(
      { commit, state, getters },
      { category, count, direction, resetProducts = true, cursor = null }
    ) {
      if (!direction) {
        commit("setWasFiltered", false);
      }
      commit("setIsFiltering", true);
      let products: IOffer[] = [];
      if (resetProducts) {
        commit("setProducts", products);
      }
      sendFetchProducts({
        commit,
        getters,
        direction,
        category,
        count,
        cursor,
        setCursor: direction ? true : !!cursor
      });
    },

    selectStore({ commit }, { name, selected }) {
      commit("setSelectStore", { name, selected });
    },

    removeProducts({ commit }) {
      commit("removeProducts");
    },

    async setLangChangeState({ commit }, [isLoading]) {
      await commit("setStores", []);
      await commit("setMinMaxPrice", [-1, -1]);
      await commit("setSort", "");
      commit("setLangChangeState", isLoading);
    },

    setSort({ commit }, [sort]) {
      commit("setSort", sort);
    },

    setFromToPrice({ commit }, [min, max]) {
      commit("setFromToPrice", [min, max]);
    },

    async filtersReset({ commit, getters }) {
      await commit("filtersReset");
    },

    async deletePrice({ commit }) {
      await commit("setMinMaxPrice", [-1, -1]);
    },

    filtersSubmit({ getters, commit, state }) {
      commit("setIsFiltering", true);
      commit("setSelectedStoresUp");
      sendFetchProducts({
        commit,
        getters,
        direction: null,
        category: state.category_id || getters.getCategories[0].category_id,
        count: 20,
        setCursor: false
      });
      if (!getters.getResetButtonDisable) {
        commit("setWasFiltered", true);
      } else {
        commit("setWasFiltered", false);
      }
    },

    setIsFiltering({ commit }, value = false) {
      commit("setIsFiltering", value);
    },
    removeStores({ commit }) {
      commit("setStores", []);
    }
  },
  mutations: {
    setWasFiltered(state, value) {
      state.wasFiltered = value;
    },
    setIsFiltering(state, value) {
      state.isFiltering = value;
    },
    setPrevNext(state, data) {
      state.hasNext = Boolean(data.haveMoreOffers);
      state.hasPrev = Boolean(data.havePrevOffers);
    },
    async filtersReset(state) {
      state.sort = "";
      state.stores = state.stores.map(store => {
        store.selected = false;
        return store;
      });
      state.price = {
        max: state.price.max,
        min: state.price.min,
        from: state.price.min,
        to: state.price.max
      };
    },

    setMinMaxPrice(state, [min, max]) {
      state.price = {
        min: parseFloat(min),
        max: parseFloat(max),
        from: parseFloat(min),
        to: parseFloat(max)
      };
    },

    setFromToPrice(state, [from, to]) {
      state.price = {
        min: state.price.min,
        max: state.price.max,
        from: parseFloat(from),
        to: parseFloat(to)
      };
    },

    setSort(state, sort) {
      state.sort = sort;
    },

    setSelectedStoresUp(state) {
      state.stores = [
        ...state.stores.filter(store => store.selected).sort(sortStores),
        ...state.stores.filter(store => !store.selected).sort(sortStores)
      ];
    },

    setSelectStore(state: IInitProducts, { name, selected }) {
      state.stores = state.stores.map(store => {
        store.selected = store.store_name === name ? selected : store.selected;
        return store;
      });
    },

    setParentCategories(state: IInitProducts, categories: ICategories[]) {
      state.categories = categories;
    },

    setCurrentCategory(state: IInitProducts, category: string) {
      state.category_id = category;
    },

    setStores(state: IInitProducts, stores: any[]) {
      state.stores = stores;
    },

    setProducts(state: IInitProducts, products: IOffer[]) {
      state.products = products;
    },

    removeProducts(state: IInitProducts) {
      state.products = [];
    },

    setProductsCount(state: IInitProducts, count: number) {
      state.productsCount = count;
    },

    setCurrentCursor(state: IInitProducts, cursor: number) {
      state.currentCursor = cursor;
    },

    setLangChangeState(state: IInitProducts, loadState: boolean) {
      state.isLangChanging = loadState;
    }
  },
  getters: {
    getProducts: (state: IInitProducts) => state.products,
    getCategories: (state: IInitProducts) => state.categories,
    getCategory: (state: IInitProducts) => state.category_id,
    getCount: (state: IInitProducts) => state.productsCount,
    getCurrentCursor: (state: IInitProducts) => state.currentCursor,
    isLangChanging: (state: IInitProducts) => state.isLangChanging,
    getStores: (state: IInitProducts) => state.stores,
    getStoresCount: (state: IInitProducts) => state.stores.length,
    getSort: (state: IInitProducts) => state.sort,
    getPrice: (state: IInitProducts) => state.price,
    getHasPrev: (state: IInitProducts) => state.hasPrev,
    getHasNext: (state: IInitProducts) => state.hasNext,
    getWasFiltered: (state: IInitProducts) => state.wasFiltered,
    getSelectedStoreNames: (state: IInitProducts) =>
      state.stores
        .filter(store => store.selected)
        .map(store => store.store_name)
        .join(","),
    getFromToPrice: (state: IInitProducts) => {
      if (
        state.price.from == state.price.min &&
        state.price.to == state.price.max
      ) {
        return {
          from: null,
          to: null
        };
      }
      return {
        from: state.price.from,
        to: state.price.to
      };
    },
    getResetButtonDisable: (state: IInitProducts) => {
      return (
        state.sort === "" &&
        !state.stores.reduce((acc, store) => store.selected || acc, false) &&
        state.price.min == state.price.from &&
        state.price.max == state.price.to
      );
    },
    getIsFiltering: (state: IInitProducts) => state.isFiltering,
    getIsFetchedFilters: (state: IInitProducts) =>
      state.stores.length && state.price.min !== -1 && state.price.max !== -1
  }
};
